import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ButtonControl, CheckboxControl, DialogControl, FieldControl, IconControl, Loading } from "spark-core-dx/components";
import { useCoreContext, useCoreStateContext } from "spark-core-dx/contexts";
import { useCorePermissions, useCoreRoute, useGenericManager } from "spark-core-dx/hooks";
import { SystemTypes } from "spark-core-dx/services";
import { ItemServiceFactory } from "../../../services/ItemServiceFactory";
import {
  getUserControlledWorkspaceVersion,
  parseWorkspaceVersion,
  normalizeUserControlledWorkspaceVersion,
  normalizeWorkspaceVersion,
} from "../workspaceVersioning";
import "./DataModelSelect.scss";

const DATA_MODEL_SERVER_EXECUTION_HOST_LOCAL = 1;
const DATA_MODEL_SERVER_EXECUTION_HOST_REMOTE_BATCH = 2;
const DATA_MODEL_LIFECYCLE_STATUS_DRAFT = 1;
const DATA_MODEL_LIFECYCLE_STATUS_PUBLISHED = 2;
const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

const getWorkspaceTitle = (workspace) =>
  String(workspace?.Name ?? "").trim() || "Untitled Model Workspace";

const getWorkspaceDescription = (workspace) =>
  String(workspace?.Description ?? "").trim() || "No description provided.";

const createBlankWorkspace = () => ({
  Name: "",
  Description: "",
  Version: "1",
  ServerExecutionHost: DATA_MODEL_SERVER_EXECUTION_HOST_REMOTE_BATCH,
});

const createWorkspaceDraft = (workspace) => ({
  Name: String(workspace?.Name ?? "").trim(),
  Description: String(workspace?.Description ?? "").trim(),
  Version: getUserControlledWorkspaceVersion(workspace?.Version),
  ServerExecutionHost: Number(workspace?.ServerExecutionHost ?? DATA_MODEL_SERVER_EXECUTION_HOST_REMOTE_BATCH),
});

const unwrapServiceResult = (response) => {
  if (Array.isArray(response)) {
    return response[0] ?? null;
  }

  return response ?? null;
};

const getValidationDisplayMessages = (result) =>
  Object.values(unwrapServiceResult(result)?.ValidationErrors ?? {})
    .map((entry) => String(entry?.DisplayMessage ?? entry?.Content ?? "").trim())
    .filter(Boolean);

const getWorkspaceLineageId = (workspace) => {
  const masterWorkspaceId = String(workspace?.MasterDataModelWorkspaceId ?? "").trim();
  if (masterWorkspaceId && masterWorkspaceId !== EMPTY_GUID) {
    return masterWorkspaceId;
  }

  return String(workspace?.UniqueId ?? workspace?._id ?? "").trim();
};

const getWorkspaceStatusPriority = (workspace) => {
  const status = Number(workspace?.Status ?? 0);
  if (status === DATA_MODEL_LIFECYCLE_STATUS_DRAFT) {
    return 0;
  }

  if (status === DATA_MODEL_LIFECYCLE_STATUS_PUBLISHED) {
    return 1;
  }

  return 2;
};

const compareWorkspaceVersionsDescending = (leftWorkspace, rightWorkspace) => {
  const leftVersion = parseWorkspaceVersion(leftWorkspace?.Version);
  const rightVersion = parseWorkspaceVersion(rightWorkspace?.Version);

  if (leftVersion.userVersion !== rightVersion.userVersion) {
    return rightVersion.userVersion - leftVersion.userVersion;
  }

  if (leftVersion.publishedVersion !== rightVersion.publishedVersion) {
    return rightVersion.publishedVersion - leftVersion.publishedVersion;
  }

  if (leftVersion.draftVersion !== rightVersion.draftVersion) {
    return rightVersion.draftVersion - leftVersion.draftVersion;
  }

  return 0;
};

const getWorkspaceTimestamp = (workspace) => {
  const rawValue =
    workspace?.PublishedDateTime
    ?? workspace?.UpdatedDate
    ?? workspace?.CreatedDate
    ?? null;
  const timestamp = rawValue ? Date.parse(rawValue) : Number.NaN;
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const shouldPreferWorkspace = (candidateWorkspace, currentWorkspace) => {
  const statusDifference = getWorkspaceStatusPriority(candidateWorkspace) - getWorkspaceStatusPriority(currentWorkspace);
  if (statusDifference !== 0) {
    return statusDifference < 0;
  }

  const versionDifference = compareWorkspaceVersionsDescending(candidateWorkspace, currentWorkspace);
  if (versionDifference !== 0) {
    return versionDifference < 0;
  }

  const candidateTimestamp = getWorkspaceTimestamp(candidateWorkspace);
  const currentTimestamp = getWorkspaceTimestamp(currentWorkspace);
  if (candidateTimestamp !== currentTimestamp) {
    return candidateTimestamp > currentTimestamp;
  }

  return String(candidateWorkspace?._id ?? "").localeCompare(String(currentWorkspace?._id ?? ""), undefined, { sensitivity: "base" }) < 0;
};

const DataModelWorkspaceSelect = () => {
  const navigate = useNavigate();
  const coreContext = useCoreContext();
  const coreState = useCoreStateContext();
  const { canCreate, canEdit, canDelete } = useCorePermissions();
  const { getRoute } = useCoreRoute();
  const itemServiceFactory = useMemo(() => new ItemServiceFactory(), []);
  const workspaceMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.DataModelWorkspace);
  const modelMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.DataModel);
  const dashboardMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.Dashboard);
  const workspaceService = useMemo(
    () => itemServiceFactory.GetItemService(itemServiceFactory.ItemServiceEnum.DataModelWorkspace, coreState, coreContext, true),
    [coreContext, coreState, itemServiceFactory]
  );
  const isSystemAdmin = useMemo(
    () => Array.isArray(coreContext?.ProfileRoles) && coreContext.ProfileRoles.some((role) => role?.IsSystemAdmin === true),
    [coreContext?.ProfileRoles]
  );
  const canCreateWorkspace = canCreate("DataModelWorkspace") || canCreate("DataModel");
  const canEditWorkspace = canEdit("DataModelWorkspace") || canEdit("DataModel");
  const canDeleteWorkspace = canDelete("DataModelWorkspace") || canDelete("DataModel");
  const modelWorkspacesBaseRoute = useMemo(
    () => getRoute("/model-workspaces/")?.RoutePath ?? getRoute("/dynamic/datamodelworkspace/")?.RoutePath ?? "/model-workspaces",
    [getRoute]
  );

  const [workspaces, setWorkspaces] = useState([]);
  const [workspaceModelCounts, setWorkspaceModelCounts] = useState({});
  const [workspaceDashboardCounts, setWorkspaceDashboardCounts] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isSavingWorkspace, setIsSavingWorkspace] = useState(false);
  const [createError, setCreateError] = useState("");
  const [workspaceDraft, setWorkspaceDraft] = useState(createBlankWorkspace());
  const [pendingEditWorkspace, setPendingEditWorkspace] = useState(null);
  const [editWorkspaceDraft, setEditWorkspaceDraft] = useState(createBlankWorkspace());
  const [editError, setEditError] = useState("");
  const [pendingDeleteWorkspace, setPendingDeleteWorkspace] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [isDeletingWorkspace, setIsDeletingWorkspace] = useState(false);
  const workspaceMgrRef = useRef(workspaceMgr);
  const modelMgrRef = useRef(modelMgr);
  const dashboardMgrRef = useRef(dashboardMgr);

  useEffect(() => {
    workspaceMgrRef.current = workspaceMgr;
  }, [workspaceMgr]);

  useEffect(() => {
    modelMgrRef.current = modelMgr;
  }, [modelMgr]);

  useEffect(() => {
    dashboardMgrRef.current = dashboardMgr;
  }, [dashboardMgr]);

  const loadWorkspaces = useCallback(async () => {
    setIsLoading(true);

    try {
      const [workspaceResult, modelResult, dashboardResult] = await Promise.all([
        workspaceMgrRef.current?.Query?.({
          model: "x => x.IsActive == true && x.IsDeleted == false",
        }),
        modelMgrRef.current?.Query?.({
          model: "x => x.IsActive == true && x.IsDeleted == false",
        }),
        dashboardMgrRef.current?.Query?.({
          model: "x => x.IsActive == true && x.IsDeleted == false",
        }),
      ]);

      const nextWorkspaces = Array.isArray(workspaceResult?.Items) ? workspaceResult.Items : [];
      const activeModels = Array.isArray(modelResult?.Items) ? modelResult.Items : [];
      const nextCounts = activeModels.reduce((accumulator, item) => {
        const workspaceId = String(item?.DataModelWorkspaceId ?? "").trim();
        if (!workspaceId) {
          return accumulator;
        }

        accumulator[workspaceId] = (accumulator[workspaceId] ?? 0) + 1;
        return accumulator;
      }, {});
      const nextDashboardCounts = (Array.isArray(dashboardResult?.Items) ? dashboardResult.Items : []).reduce((accumulator, item) => {
        const workspaceId = String(item?.DataModelWorkspaceId ?? "").trim();
        if (!workspaceId) {
          return accumulator;
        }

        accumulator[workspaceId] = (accumulator[workspaceId] ?? 0) + 1;
        return accumulator;
      }, {});

      setWorkspaces(nextWorkspaces);
      setWorkspaceModelCounts(nextCounts);
      setWorkspaceDashboardCounts(nextDashboardCounts);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadWorkspaces();
  }, [loadWorkspaces]);

  const getWorkspaceReferenceSummary = useCallback((workspaceId) => {
    const normalizedWorkspaceId = String(workspaceId ?? "").trim();
    return {
      modelCount: workspaceModelCounts[normalizedWorkspaceId] ?? 0,
      dashboardCount: workspaceDashboardCounts[normalizedWorkspaceId] ?? 0,
    };
  }, [workspaceDashboardCounts, workspaceModelCounts]);

  const openWorkspace = useCallback((workspaceId) => {
    if (!workspaceId) {
      return;
    }

    navigate(`${modelWorkspacesBaseRoute}/${workspaceId}`);
  }, [modelWorkspacesBaseRoute, navigate]);

  const closeCreateDialog = useCallback(() => {
    if (isSavingWorkspace) {
      return;
    }

    setIsCreateDialogOpen(false);
    setCreateError("");
    setWorkspaceDraft(createBlankWorkspace());
  }, [isSavingWorkspace]);

  const handleCreateWorkspace = useCallback(async () => {
    const name = String(workspaceDraft?.Name ?? "").trim();
    const version = normalizeUserControlledWorkspaceVersion(workspaceDraft?.Version);
    if (!name) {
      setCreateError("Workspace name is required.");
      return;
    }

    if (!version) {
      setCreateError("Workspace version is required.");
      return;
    }

    setIsSavingWorkspace(true);
    setCreateError("");

    try {
      const response = await workspaceService.save({
        Name: name,
        Description: String(workspaceDraft?.Description ?? "").trim(),
        Version: version,
        ServerExecutionHost: isSystemAdmin
          ? Number(workspaceDraft?.ServerExecutionHost ?? DATA_MODEL_SERVER_EXECUTION_HOST_REMOTE_BATCH)
          : DATA_MODEL_SERVER_EXECUTION_HOST_REMOTE_BATCH,
        IsActive: true,
        IsDeleted: false,
      });
      const normalizedResponse = Array.isArray(response) ? response[0] : response;
      if (!normalizedResponse?.Success) {
        setCreateError(
          normalizedResponse?.DisplayMessage ??
          normalizedResponse?.Message?.DisplayMessage ??
          "Unable to create model workspace."
        );
        return;
      }

      const savedWorkspace = normalizedResponse?.Items?.first?.() ?? normalizedResponse?.Items?.[0] ?? null;
      await loadWorkspaces();
      closeCreateDialog();
      if (savedWorkspace?._id) {
        openWorkspace(savedWorkspace._id);
      }
    } catch (error) {
      console.error("Model workspace create failed:", error);
      setCreateError("Unable to create model workspace.");
    } finally {
      setIsSavingWorkspace(false);
    }
  }, [closeCreateDialog, isSystemAdmin, loadWorkspaces, openWorkspace, workspaceDraft, workspaceService]);

  const closeEditDialog = useCallback(() => {
    if (isSavingWorkspace) {
      return;
    }

    setPendingEditWorkspace(null);
    setEditWorkspaceDraft(createBlankWorkspace());
    setEditError("");
  }, [isSavingWorkspace]);

  const openEditDialog = useCallback((workspace) => {
    setPendingEditWorkspace(workspace ?? null);
    setEditWorkspaceDraft(createWorkspaceDraft(workspace));
    setEditError("");
  }, []);

  const handleSaveWorkspaceEdit = useCallback(async () => {
    const workspaceName = String(editWorkspaceDraft?.Name ?? "").trim();
    const version = normalizeUserControlledWorkspaceVersion(editWorkspaceDraft?.Version, pendingEditWorkspace?.Version);

    if (!workspaceName) {
      setEditError("Workspace name is required.");
      return;
    }

    if (!version) {
      setEditError("Workspace version is required.");
      return;
    }

    if (!pendingEditWorkspace?._id) {
      setEditError("A model workspace is required.");
      return;
    }

    setIsSavingWorkspace(true);
    setEditError("");

    try {
      const rawResponse = await workspaceService.save({
        ...pendingEditWorkspace,
        _id: pendingEditWorkspace?._id,
        UniqueId: pendingEditWorkspace?.UniqueId ?? pendingEditWorkspace?._id,
        Name: workspaceName,
        Description: String(editWorkspaceDraft?.Description ?? "").trim(),
        Version: version,
        ServerExecutionHost: isSystemAdmin
          ? Number(editWorkspaceDraft?.ServerExecutionHost ?? DATA_MODEL_SERVER_EXECUTION_HOST_REMOTE_BATCH)
          : Number(pendingEditWorkspace?.ServerExecutionHost ?? DATA_MODEL_SERVER_EXECUTION_HOST_LOCAL),
      });
      const response = unwrapServiceResult(rawResponse);

      if (!response?.Success) {
        setEditError(
          getValidationDisplayMessages(response)[0]
          || response?.DisplayMessage
          || response?.Message?.DisplayMessage
          || "Unable to save model workspace."
        );
        return;
      }

      await loadWorkspaces();
      closeEditDialog();
    } catch (error) {
      console.error("Model workspace update failed:", error);
      setEditError("Unable to save model workspace.");
    } finally {
      setIsSavingWorkspace(false);
    }
  }, [closeEditDialog, editWorkspaceDraft, isSystemAdmin, loadWorkspaces, pendingEditWorkspace, workspaceService]);

  const closeDeleteDialog = useCallback(() => {
    if (isDeletingWorkspace) {
      return;
    }

    setPendingDeleteWorkspace(null);
    setDeleteError("");
  }, [isDeletingWorkspace]);

  const handleDeleteWorkspace = useCallback(async () => {
    const workspaceId = String(pendingDeleteWorkspace?._id ?? "").trim();
    if (!workspaceId) {
      return;
    }

    const referenceSummary = getWorkspaceReferenceSummary(workspaceId);
    if ((referenceSummary.modelCount ?? 0) > 0 || (referenceSummary.dashboardCount ?? 0) > 0) {
      setDeleteError(
        `Remove ${referenceSummary.modelCount.toLocaleString()} model(s) and ${referenceSummary.dashboardCount.toLocaleString()} dashboard(s) from this workspace before deleting it.`
      );
      return;
    }

    setIsDeletingWorkspace(true);
    setDeleteError("");

    try {
      const rawResponse = await workspaceService.delete(workspaceId);
      const response = unwrapServiceResult(rawResponse);

      if (response && response?.Success === false) {
        setDeleteError(
          getValidationDisplayMessages(response)[0]
          || response?.DisplayMessage
          || response?.Message?.DisplayMessage
          || "Unable to delete model workspace."
        );
        return;
      }

      await loadWorkspaces();
      closeDeleteDialog();
    } catch (error) {
      console.error("Model workspace delete failed:", error);
      setDeleteError("Unable to delete model workspace.");
    } finally {
      setIsDeletingWorkspace(false);
    }
  }, [closeDeleteDialog, getWorkspaceReferenceSummary, loadWorkspaces, pendingDeleteWorkspace?._id, workspaceService]);

  const pendingDeleteReferenceSummary = useMemo(
    () => getWorkspaceReferenceSummary(String(pendingDeleteWorkspace?._id ?? "").trim()),
    [getWorkspaceReferenceSummary, pendingDeleteWorkspace?._id]
  );

  const visibleWorkspaces = useMemo(() => {
    const latestWorkspaceByLineage = new Map();

    workspaces.forEach((workspace) => {
      const lineageId = getWorkspaceLineageId(workspace);
      if (!lineageId) {
        return;
      }

      const currentWorkspace = latestWorkspaceByLineage.get(lineageId);
      if (!currentWorkspace || shouldPreferWorkspace(workspace, currentWorkspace)) {
        latestWorkspaceByLineage.set(lineageId, workspace);
      }
    });

    return Array.from(latestWorkspaceByLineage.values());
  }, [workspaces]);

  return (
    <div className="data-model-select-screen">
      <DialogControl
        className="data-model-dialog"
        openDialog={isCreateDialogOpen}
        title="Create Model Workspace"
        okText={isSavingWorkspace ? "Saving..." : "Create"}
        disableOk={isSavingWorkspace}
        onOk={handleCreateWorkspace}
        onCancel={closeCreateDialog}
      >
        <FieldControl
          fieldMetadata={{
            FieldType: SystemTypes.UIFieldType.TextBox,
            FieldName: "Name",
            DisplayName: "Workspace Name",
            IsRequired: true,
          }}
          groupId="modelWorkspace"
          value={workspaceDraft.Name}
          onChange={(value) => setWorkspaceDraft((prev) => ({ ...prev, Name: value }))}
        />
        <FieldControl
          fieldMetadata={{
            FieldType: SystemTypes.UIFieldType.TextArea,
            FieldName: "Description",
            DisplayName: "Description",
            IsRequired: false,
          }}
          groupId="modelWorkspace"
          value={workspaceDraft.Description}
          onChange={(value) => setWorkspaceDraft((prev) => ({ ...prev, Description: value }))}
        />
        <FieldControl
          fieldMetadata={{
            FieldType: SystemTypes.UIFieldType.TextBox,
            FieldName: "Version",
            DisplayName: "Workspace Version",
            IsRequired: true,
          }}
          groupId="modelWorkspace"
          value={workspaceDraft.Version}
          onChange={(value) => setWorkspaceDraft((prev) => ({ ...prev, Version: value }))}
        />
        {isSystemAdmin ? (
          <>
            <div className="dialog-checkbox">
              <CheckboxControl
                value={Number(workspaceDraft?.ServerExecutionHost ?? DATA_MODEL_SERVER_EXECUTION_HOST_LOCAL) === DATA_MODEL_SERVER_EXECUTION_HOST_REMOTE_BATCH}
                onChange={() => setWorkspaceDraft((prev) => ({
                  ...prev,
                  ServerExecutionHost:
                    Number(prev?.ServerExecutionHost ?? DATA_MODEL_SERVER_EXECUTION_HOST_LOCAL) === DATA_MODEL_SERVER_EXECUTION_HOST_REMOTE_BATCH
                      ? DATA_MODEL_SERVER_EXECUTION_HOST_LOCAL
                      : DATA_MODEL_SERVER_EXECUTION_HOST_REMOTE_BATCH,
                }))}
              />
              <span>Use Remote Batch Host For Server Mode</span>
            </div>
            <div className="dialog-text dialog-text--compact">
              When enabled, server-mode Virtual DB load, query, build, and publish operations are routed through the assigned remote batch host instead of the local app server.
            </div>
          </>
        ) : null}
        {createError ? <div className="dialog-text" style={{ color: "var(--error)" }}>{createError}</div> : null}
      </DialogControl>

      <DialogControl
        className="data-model-dialog"
        openDialog={!!pendingEditWorkspace}
        title="Edit Model Workspace"
        okText={isSavingWorkspace ? "Saving..." : "Save"}
        disableOk={isSavingWorkspace}
        onOk={handleSaveWorkspaceEdit}
        onCancel={closeEditDialog}
      >
        <FieldControl
          fieldMetadata={{
            FieldType: SystemTypes.UIFieldType.TextBox,
            FieldName: "Name",
            DisplayName: "Workspace Name",
            IsRequired: true,
          }}
          groupId="editModelWorkspace"
          value={editWorkspaceDraft.Name}
          onChange={(value) => setEditWorkspaceDraft((prev) => ({ ...prev, Name: value }))}
        />
        <FieldControl
          fieldMetadata={{
            FieldType: SystemTypes.UIFieldType.TextArea,
            FieldName: "Description",
            DisplayName: "Description",
            IsRequired: false,
          }}
          groupId="editModelWorkspace"
          value={editWorkspaceDraft.Description}
          onChange={(value) => setEditWorkspaceDraft((prev) => ({ ...prev, Description: value }))}
        />
        <FieldControl
          fieldMetadata={{
            FieldType: SystemTypes.UIFieldType.TextBox,
            FieldName: "Version",
            DisplayName: "Workspace Version",
            IsRequired: true,
          }}
          groupId="editModelWorkspace"
          value={editWorkspaceDraft.Version}
          onChange={(value) => setEditWorkspaceDraft((prev) => ({ ...prev, Version: value }))}
        />
        {isSystemAdmin ? (
          <>
            <div className="dialog-checkbox">
              <CheckboxControl
                value={Number(editWorkspaceDraft?.ServerExecutionHost ?? DATA_MODEL_SERVER_EXECUTION_HOST_LOCAL) === DATA_MODEL_SERVER_EXECUTION_HOST_REMOTE_BATCH}
                onChange={() => setEditWorkspaceDraft((prev) => ({
                  ...prev,
                  ServerExecutionHost:
                    Number(prev?.ServerExecutionHost ?? DATA_MODEL_SERVER_EXECUTION_HOST_LOCAL) === DATA_MODEL_SERVER_EXECUTION_HOST_REMOTE_BATCH
                      ? DATA_MODEL_SERVER_EXECUTION_HOST_LOCAL
                      : DATA_MODEL_SERVER_EXECUTION_HOST_REMOTE_BATCH,
                }))}
              />
              <span>Use Remote Batch Host For Server Mode</span>
            </div>
            <div className="dialog-text dialog-text--compact">
              When enabled, server-mode Virtual DB load, query, build, and publish operations are routed through the assigned remote batch host instead of the local app server.
            </div>
          </>
        ) : null}
        {editError ? <div className="dialog-text" style={{ color: "var(--error)" }}>{editError}</div> : null}
      </DialogControl>

      <DialogControl
        className="data-model-dialog"
        openDialog={!!pendingDeleteWorkspace}
        title="Delete Model Workspace"
        okText={isDeletingWorkspace ? "Deleting..." : "Delete"}
        disableOk={isDeletingWorkspace || (pendingDeleteReferenceSummary.modelCount ?? 0) > 0 || (pendingDeleteReferenceSummary.dashboardCount ?? 0) > 0}
        onOk={handleDeleteWorkspace}
        onCancel={closeDeleteDialog}
      >
        <div className="dialog-text">
          Please confirm that you want to delete{" "}
          <strong>{getWorkspaceTitle(pendingDeleteWorkspace)}</strong>.
        </div>
        {(pendingDeleteReferenceSummary.modelCount ?? 0) > 0 || (pendingDeleteReferenceSummary.dashboardCount ?? 0) > 0 ? (
          <div className="dialog-text" style={{ color: "var(--error)" }}>
            This workspace is still in use by {(pendingDeleteReferenceSummary.modelCount ?? 0).toLocaleString()} model(s) and {(pendingDeleteReferenceSummary.dashboardCount ?? 0).toLocaleString()} dashboard(s).
          </div>
        ) : null}
        {deleteError ? <div className="dialog-text" style={{ color: "var(--error)" }}>{deleteError}</div> : null}
      </DialogControl>

      <div className="data-model-select-header">
        <div>
          <div className="eyebrow">Data Management</div>
          <h1>Choose a model workspace</h1>
          <p>Select a workspace to manage its data models.</p>
        </div>
        {canCreateWorkspace ? (
          <ButtonControl
            value="New Workspace"
            type="okay"
            onClick={() => setIsCreateDialogOpen(true)}
          />
        ) : null}
      </div>

      {isLoading ? (
        <div className="data-model-select-loading">
          <Loading />
        </div>
      ) : (
        <div className="data-model-select-grid">
          {visibleWorkspaces.map((workspace) => {
            const workspaceId = String(workspace?._id ?? "").trim();
            const referenceSummary = getWorkspaceReferenceSummary(workspaceId);
            return (
              <div key={workspaceId} className="data-model-select-card">
                <button
                  className="data-model-select-card-main"
                  type="button"
                  onClick={() => openWorkspace(workspaceId)}
                >
                  <div className="data-model-select-card-header">
                    <div className="data-model-select-card-title-row">
                      <IconControl iconKey="schema" />
                      <div className="data-model-select-card-title">{getWorkspaceTitle(workspace)}</div>
                    </div>
                    <div className="data-model-state-chip">
                      v{normalizeWorkspaceVersion(workspace?.Version)}
                    </div>
                  </div>
                  <div className="data-model-select-card-description">{getWorkspaceDescription(workspace)}</div>
                </button>
                <div className="data-model-select-card-footer">
                  <span>{referenceSummary.modelCount.toLocaleString()} Models | {referenceSummary.dashboardCount.toLocaleString()} Dashboards</span>
                  {canEditWorkspace || canDeleteWorkspace ? (
                    <div className="data-model-select-card-actions">
                      {canEditWorkspace ? (
                        <button
                          className="data-model-select-action-button"
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            openEditDialog(workspace);
                          }}
                        >
                          Edit
                        </button>
                      ) : null}
                      {canDeleteWorkspace ? (
                        <button
                          className="data-model-select-action-button data-model-select-delete-button"
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setPendingDeleteWorkspace(workspace);
                            setDeleteError("");
                          }}
                        >
                          Delete
                        </button>
                      ) : null}
                    </div>
                  ) : (
                    <span>Open</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!isLoading && (visibleWorkspaces?.length ?? 0) === 0 ? (
        <div className="data-model-select-empty">
          <IconControl iconKey="schema" />
          <div className="title">No model workspaces yet</div>
          <div className="description">Create the first model workspace to start organizing data models.</div>
          {canCreateWorkspace ? (
            <ButtonControl
              value="Create Model Workspace"
              type="okay"
              className="create-data-model-btn"
              onClick={() => setIsCreateDialogOpen(true)}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default DataModelWorkspaceSelect;
